import API from "serviceshift-ui/api-client";

// Shapes from the ENG-900 API contract (Admin::V4 zapier endpoints, ENG-947).
// The panel is read-only: there are deliberately no write actions here.

export type ZapierDeliveryStatus =
  "success" | "failure" | "unsubscribed" | "cancelled" | "queued";

export interface ZapierTrigger {
  key: string;
  label: string;
}

// legacy: a zapier_subscriptions row (integer id, legacy payloads). engine: a
// Zap on the catalog Zapier app, delivered by the webhook engine (id is the
// endpoint's whep_ public id, payloads are the webhook event envelope).
export type ZapierSubscriptionSource = "legacy" | "engine";

// Why a Zap stopped. Legacy rows carry gone, unsubscribed, tenant_cancelled or
// null; engine rows can also be stopped by Sera (failing, integration_off,
// admin) or paused.
export type ZapierStoppedReason =
  | "gone"
  | "unsubscribed"
  | "tenant_cancelled"
  | "failing"
  | "integration_off"
  | "admin"
  | "paused";

// The Webhooks hub's 24-hour delivery counts, engine rows only.
export interface ZapierEngineStats24h {
  pending: number;
  retrying: number;
  succeeded: number;
  failed: number;
  cancelled: number;
  success_rate: number | null;
}

export interface ZapierSubscription {
  id: number | string;
  source?: ZapierSubscriptionSource;
  payload_style?: "legacy" | "envelope";
  state: "active" | "stopped";
  trigger: ZapierTrigger;
  destination: {
    host: string;
    zapier_account: string | null;
    fingerprint: string;
  };
  created_at: string;
  archived_at: string | null;
  archived_reason: ZapierStoppedReason | null;
  last_delivery: {
    at: string;
    status: ZapierDeliveryStatus;
    response_code: number | null;
  } | null;
  counts_30d: Record<ZapierDeliveryStatus, number>;
  stats_24h?: ZapierEngineStats24h | null;
}

export interface ZapierSubscriptionsResponse {
  data: ZapierSubscription[];
  meta: { window_days: number; generated_at: string };
}

export interface ZapierDeliveryOutcome {
  category: string;
  title: string;
  hint: string | null;
}

export interface ZapierDelivery {
  id: number | string;
  source?: ZapierSubscriptionSource;
  // The engine delivery's own status (engine rows only): retrying means a
  // failed delivery that will be tried again.
  engine_status?: string;
  attempts_count?: number;
  created_at: string;
  status: ZapierDeliveryStatus;
  response_code: number | null;
  outcome: ZapierDeliveryOutcome;
  target_object_id: number | null;
  trigger: ZapierTrigger;
}

export interface ZapierDeliveryPagination {
  page: number;
  per_page: number;
  total_pages: number;
  total_objects: number;
  from: number;
  to: number;
}

export interface ZapierDeliveriesResponse {
  data: ZapierDelivery[];
  links: Record<string, string>;
  meta: { pagination: ZapierDeliveryPagination };
}

// One HTTP try of an engine delivery.
export interface ZapierDeliveryAttempt {
  number: number;
  started_at: string;
  response_code: number | null;
  outcome: ZapierDeliveryOutcome;
  response_excerpt: string | null;
}

export interface ZapierDeliveryDetail {
  id: number | string;
  payload_style?: "legacy" | "envelope";
  created_at: string;
  status: ZapierDeliveryStatus;
  response_code: number | null;
  outcome: ZapierDeliveryOutcome;
  payload: unknown;
  response_excerpt: string | null;
  zap_request_id: string | null;
  zap_attempt_id: string | null;
  attempts?: ZapierDeliveryAttempt[];
}

export interface FetchZapierDeliveriesParams {
  subscriptionId: number | string;
  status?: ZapierDeliveryStatus | null;
  page?: number;
  perPage?: number;
}

const BASE = "/admin/v4/zapier";

export default {
  async fetchZapierSubscriptions(): Promise<ZapierSubscriptionsResponse> {
    const response = await API.makeRequest<ZapierSubscriptionsResponse>(
      `${BASE}/subscriptions`
    );
    return response.data;
  },

  // `status`, `page` and `per_page` are plain query params per the contract,
  // not the `filter[...]` family lib/pagination builds, which is why this does
  // not go through that class.
  async fetchZapierSubscriptionDeliveries(
    _: unknown,
    {
      subscriptionId,
      status,
      page = 1,
      perPage = 25
    }: FetchZapierDeliveriesParams
  ): Promise<ZapierDeliveriesResponse> {
    const query = new URLSearchParams({
      page: String(page),
      per_page: String(perPage)
    });
    if (status) query.set("status", status);

    const response = await API.makeRequest<ZapierDeliveriesResponse>(
      `${BASE}/subscriptions/${subscriptionId}/deliveries?${query.toString()}`
    );
    return response.data;
  },

  async fetchZapierDelivery(
    _: unknown,
    id: number | string
  ): Promise<ZapierDeliveryDetail> {
    const response = await API.makeRequest<{ data: ZapierDeliveryDetail }>(
      `${BASE}/deliveries/${id}`
    );
    return response.data.data;
  }
};
